import Box from '@mui/material/Box'
import { LargeCard } from 'Components/Cards/Large'
import { TextFieldWithLabel } from 'Components/TextField'
import { useState } from 'react'
import { type RowData } from 'Components/Table/DataTable/DataTable.model'
import { Alert, Button, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router'
import { useAuth } from 'hooks/use-auth'
import { DuplicateIcon } from 'Assets/SVGs/Duplicate'
import { OpenInNew } from '@mui/icons-material'
import { useGetAllDuplicatesQuery, useGetDuplicateStatsQuery, useLazyLoadChildDuplicateQuery } from 'slices/duplicateChildSlice'
import * as AppRoutes from 'Pages/Routes'
import { DuplicateTable } from 'Components/DuplicateHandling/DuplicateTable'
import { Role } from 'core/types'

export interface DuplicateStats {
  newDuplicatesLast7Days: number
  totalUnresolvedDuplicates: number
  submittedLast90Days: number
}

export function RecentlyCreatedDuplicates (): JSX.Element {
  const [nameFilter, setNameFilter] = useState<string | null>(null)
  const { user } = useAuth()
  const nav = useNavigate()
  const [getDuplicateDetails] = useLazyLoadChildDuplicateQuery()
  const { data: duplicateList, isLoading: isLoadingDuplicates } = useGetAllDuplicatesQuery({ newOnly: true, unresolvedOnly: true })
  const { data: stats, isLoading: isLoadingStats } = useGetDuplicateStatsQuery()
  const showISD = (user?.securityLevel ?? '') === Role.MIECC_ADMIN

  const goToShowAll = (): void => {
    nav(AppRoutes.DUPLICATE_LIST)
  }
  const rowClick = async (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): Promise<void> => {
    const { data: childDupeRecord } = await getDuplicateDetails(row.duplicateID)
    if (childDupeRecord != null) {
      nav(AppRoutes.DUPE_MANAGEMENT)
    }
  }

  const cardContent = <>
    {duplicateList != null && duplicateList.length === 0 && <Box>
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19.9998 8.33301C12.6482 8.33301 6.6665 14.3147 6.6665 21.6663C6.6665 29.018 12.6482 34.9997 19.9998 34.9997C27.3515 34.9997 33.3332 29.018 33.3332 21.6663C33.3332 14.3147 27.3515 8.33301 19.9998 8.33301ZM19.9998 31.6663C14.4848 31.6663 9.99984 27.1813 9.99984 21.6663C9.99984 16.1513 14.4848 11.6663 19.9998 11.6663C25.5148 11.6663 29.9998 16.1513 29.9998 21.6663C29.9998 27.1813 25.5148 31.6663 19.9998 31.6663Z" fill="#722A82" />
        <path d="M18.3333 14.9997H21.6667V23.333H18.3333V14.9997ZM15 3.33301H25V6.66634H15V3.33301ZM32.155 12.8447L28.8217 9.51134L31.1783 7.15467L34.5117 10.488L32.155 12.8447Z" fill="#722A82" />
      </svg>
    </Box>}
    {duplicateList != null && duplicateList.length > 0 && <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', paddingRight: '30px' }}>
        <Box sx={{ minWidth: '300px' }}>
          <TextFieldWithLabel
            label='Child Name'
            onChange={(e) => { setNameFilter(e.target.value) }}
            name='childName'
            maxWidth='300px'
            value={nameFilter}
            data-testid="name-search"
            placeHolder='Last or First Name'
          />
        </Box>

        <Button variant='contained' size='large' endIcon={<OpenInNew />} sx={{ paddingX: '40px' }} onClick={goToShowAll}>Show All</Button>
      </Box>

      <Box sx={{ paddingRight: '30px' }}>
        <DuplicateTable
          name='duplicateList'
          duplicateList={duplicateList ?? []}
          searchCriteria={{ childName: nameFilter ?? '', caregiverName: '', source: 'All', isResolved: 'All', isdID: null }}
          rowClick={rowClick}
          showISD={showISD}
        />
      </Box>

      <Box sx={{ paddingRight: '30px' }}>
        <Alert severity="info" icon={<></>} sx={{ border: '1px solid #3694BC', borderRadius: '12px', padding: '10px 20px' }}>
          <Stack direction='row' spacing={2}>
            <Box sx={{ paddingTop: '5px' }}>
              <DuplicateIcon fill='#7136BC' />
            </Box>
            <Stack>
              <Stack direction='row' spacing={1}>
                <Typography fontWeight='700'>{stats?.newDuplicatesLast7Days}</Typography>
                <Typography>duplicates created in the last 7 days {user?.securityLevel === Role.MIECC_ADMIN ? '' : 'for your ISD'}</Typography>
              </Stack>
              <Stack direction='row' spacing={1}>
                <Typography fontWeight='700'>{stats?.totalUnresolvedDuplicates}</Typography>
                <Typography>total unresolved duplicates {user?.securityLevel === Role.MIECC_ADMIN ? '' : 'for your ISD'}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </Alert>
      </Box>
    </>}
  </>

  if (isLoadingDuplicates || isLoadingStats || user == null) {
    return <></>
  } else {
    return <>
      <LargeCard header='Recently Created Duplicates' headerClassName='info-box-header' sx={{ width: '100%' }} content={cardContent} />
    </>
  }
}
